import {
  computed,
  getCurrentInstance,
  type ComputedRef,
  type MaybeRefOrGetter,
  toValue,
} from "vue";
import { useRoute, type RouteLocationNormalized } from "vue-router";
import { getJobIdFromUrl } from "@/utils/jobIdUrl";
import { TREATMENT_PLAN_JOB_ORIGIN } from "@/api/models/treatmentPlan/jobConstants";
import type { TTreatmentPlanJobSignal } from "@/api/models/treatmentPlan/job/types";
import type { useTreatmentPlanStore } from "@/stores/treatmentPlanStore";

type TJobLookup = () => Pick<ReturnType<typeof useTreatmentPlanStore>, "getAvailableJobId">;

let jobLookup: TJobLookup | undefined;

/**
 * Supplies the treatment-plan store to the query layer.
 *
 * Injected at app start rather than imported here: the store pulls in the treatment-plan
 * service barrel, and a static import would attach that whole graph to every generic query.
 * See `src/plugins/queryJobLookup.ts`.
 */
export function provideJobLookup(lookup: TJobLookup): void {
  jobLookup = lookup;
}

function getJobLookup(): TJobLookup {
  if (!jobLookup) {
    throw new Error(
      "Job lookup was not provided — call provideJobLookup() during app setup before using jobSignal."
    );
  }
  return jobLookup;
}

/** The query-param name for the treatment-plan job a request is authorized under. */
export const JOB_ID_FILTER_KEY = TREATMENT_PLAN_JOB_ORIGIN;

/**
 * A filters shape plus the `job_id` a job-authorized query sends.
 *
 * Declare a job-authorized query's filters with this, and list `job_id` in `neededKey` so the
 * query holds until the job resolves rather than firing a request that 403s.
 */
export type TWithJobId<TFilters> = TFilters & { [TREATMENT_PLAN_JOB_ORIGIN]?: string };

/**
 * Resolves the treatment-plan job a request is authorized under.
 *
 * Given a `jobSignal`, the job is looked up in the treatment-plan store — the authoritative
 * source, and the only one that can tell whether the available job is the *right* one for this
 * query. Without a signal it falls back to the URL, which is how the app carries a job between
 * pages; that fallback cannot tell one job type from another, so prefer naming the signal.
 *
 * The route is resolved once, during setup: calling `useRoute()` inside a fetcher would throw,
 * since fetchers run outside any setup context.
 */
export function useJobIdQueryParam(
  jobSignal?: MaybeRefOrGetter<TTreatmentPlanJobSignal | true | undefined>
): ComputedRef<string | undefined> {
  // Outside a component, or with no router installed, there is no route to read — treat that as
  // "no job_id" rather than throwing. Only queries that opt in are affected by the result.
  let route: RouteLocationNormalized | undefined;
  if (getCurrentInstance()) {
    try {
      route = useRoute();
    } catch {
      route = undefined;
    }
  }

  const store = getCurrentInstance() && jobSignal ? getJobLookup()() : undefined;

  return computed(() => {
    const signal = toValue(jobSignal);
    if (signal && signal !== true) {
      return store?.getAvailableJobId(signal).value;
    }
    return (route ? getJobIdFromUrl(route) : null) ?? undefined;
  });
}
